import React from "react";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Pagination from "react-bootstrap/Pagination";
import Loading from "../../../components/loader/Loading";
import { TableSkeleton } from "../../../components/Dashboard/SkeletonLoaders";
import { Search } from "lucide-react";
import moment from "moment";

const LandlordMetricsTable = ({
  landlordMetrics,
  loadingTable,
  onViewDetails,
  currentPage,
  setCurrentPage,
  itemsPerPage = 10,
  filters,
  handleSearchChange,
  handleSearchFocus,
  handleSearchClick,
  handleSelectLandlord,
  clearLandlordFilter,
  showLandlordDropdown,
  landlordSearchResults,
  searchingLandlords,
  searchRef,
  handleFilterChange
}) => {
  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = landlordMetrics.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(landlordMetrics.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPagination = () => {
    const items = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted small">
          Showing <strong>{indexOfFirstItem + 1}</strong> to{" "}
          <strong>{Math.min(indexOfLastItem, landlordMetrics.length)}</strong> of{" "}
          <strong>{landlordMetrics.length}</strong> landlords
        </div>
        <Pagination className="tenants-pagination mb-0">{items}</Pagination>
      </div>
    );
  };

  return (
    <div className="tenants-container metrics-theme-table">
      <div className="tenants-card">
        <div className="p-4">
          <p className="metrics-table-caption mb-0">
            PRICING METRICS — AGGREGATED AT LANDLORD OWNER + ALL THEIR WORKSPACES
          </p>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loadingTable ? (
            <TableSkeleton rows={itemsPerPage} />
          ) : (
            <Table responsive hover className="tenants-table mb-0">
              <thead>
                <tr>
                  <th>LANDLORD</th>
                  <th>TYPE</th>
                  <th>WORKSPACE</th>
                  <th className="text-center">PROFILES</th>
                  <th className="text-center">TENANT CHECKS</th>
                  <th className="text-center">ACTION</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((metric, index) => (
                    <tr key={metric.landlord?._id || index}>
                      <td>
                        <div className="tenant-name-stack">
                          <span className="tenant-name">
                            {metric.landlord?.fname_companyName} {metric.landlord?.lname}
                          </span>
                          <span className="tenant-email">
                            {metric.landlord?.email || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="fw-500">{metric.type || "N/A"}</div>
                      </td>
                      <td>
                        <div className="fw-500">{metric.workspaceName || "N/A"}</div>
                        {metric.workspaces && metric.workspaces.length > 1 && (
                          <div className="text-muted small" style={{ fontSize: "10px" }}>
                            {metric.workspaces.slice(0, 2).map(ws => ws?.name || 'Unknown').join(', ')}
                            {metric.workspaces.length > 2 && ` +${metric.workspaces.length - 2} more`}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="fw-600">{metric.tenantCount || 0}</span>
                        {metric.deletedTenantCount > 0 && (
                          <div className="text-muted small" style={{ fontSize: "10px" }}>
                            ({metric.deletedTenantCount} deleted)
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <span className="fw-600">{metric.tenantChecks || 0}</span>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="white"
                          className="tenant-action-btn"
                          onClick={() => onViewDetails(metric)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-muted">
                      No Data Found!
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        <div className="p-3">
          {totalPages > 1 && !loadingTable && landlordMetrics.length > 0 && renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default LandlordMetricsTable;
